import Store from 'store/store';
import { scrollStages } from 'helpers/scrollStages';
import { NEXT, PREV, LANDING } from 'helpers/constants';
import { getLandingAssetsToLoad } from 'helpers/scene';
import { getAssetsToLoad } from 'helpers/ar';
import { getSplashAssetsToLoad } from 'helpers/splash';
import { getLoadingAssetsToLoad } from 'helpers/loader';

Store.on('MAIN_MOUNTED', () => {
	Store.get().main.set({ scrollStage: scrollStages[0] });
});

Store.on('TOGGLE_MUTE', (bool) => {
	Store.get().main.set({ audioMuted: bool });
});

Store.on('LOADING_CLOUD_LOADED', (asset) => {
	const loadedClouds = Store.get().main.loadingCloudsLoaded || [];
	const newLoadedAssets = [...loadedClouds, asset];
	Store.get().main.set({ loadingCloudsLoaded: newLoadedAssets });
	const neededAssets = getLoadingAssetsToLoad();
	if (newLoadedAssets.length >= neededAssets.length) {
		Store.emit('ALL_LOADING_CLOUDS_LOADED');
	}
});

Store.on('ALL_LOADING_CLOUDS_LOADED', () => {
	Store.get().main.set({ loadingCloudsReady: true });
});

Store.on('LANDING_ASSET_LOADED', (asset) => {
	const loadedAssets = Store.get().main.landingAssetsLoaded || [];
	const newLoadedAssets = [...loadedAssets, asset];
	Store.get().main.set({ landingAssetsLoaded: newLoadedAssets });
	const neededAssets = getLandingAssetsToLoad();
	if (newLoadedAssets.length >= neededAssets.length) {
		Store.emit('ALL_LANDING_ASSETS_LOADED');
	}
});

Store.on('ALL_LANDING_ASSETS_LOADED', () => {
	Store.get().main.set({ landingReady: true });
});

Store.on('REVEAL_LANDING', () => {
	Store.get().main.set({ landingRevealed: true });
});

Store.on('TOGGLE_FOLDING_MENU', (bool) => {
	Store.get().main.set({ menuOpen: bool });
});

Store.on('UPDATE_SCROLL_STAGE', (stage) => {
	const currentStage = Store.get().main.scrollStage;
	const scrollLocked = Store.get().main.scrollLocked;
	if (scrollLocked) return;
	const currentStageIndex = scrollStages.indexOf(currentStage);
	let newStage = stage;
	if (stage === NEXT) {
		newStage = scrollStages[Math.min(currentStageIndex + 1, scrollStages.length - 1)];
	}
	if (stage === PREV) {
		newStage = scrollStages[Math.max(currentStageIndex - 1, 0)];
	}
	const newStageIndex = scrollStages.indexOf(newStage);

	if (currentStage === newStage) return;

	if ((stage === NEXT || stage === PREV) && newStage.startsWith('habitat')) {
		Store.emit('TRACK_LANDING_GO_TO_HABITAT', 'landing-scroll');
	}

	Store.get().main.set({
		scrollStage: newStage,
		scrollDirection: newStageIndex > currentStageIndex ? NEXT : PREV,
		scrollLocked: true
	});
});

Store.on('LOCK_SCROLL', (bool) => {
	Store.get().main.set({ scrollLocked: bool });
});

Store.on('OPEN_HABITAT_PAPER', (habitatNum) => {
	Store.get().main.set({ habitatPaperOpen: habitatNum });
});

Store.on('CLOSE_HABITAT_PAPER', () => {
	Store.get().main.set({
		habitatPaperOpen: null
	});
	Store.emit('LOCK_SCROLL', false);
});

Store.on('FAST_CLOSE_HABITAT_PAPER', (habitatPaperOpen) => {
	Store.emit('CLOSE_HABITAT_PAPER', habitatPaperOpen);
});

Store.on('TOGGLE_ORIGAMI_INSTRUCTIONS', () => {
	const { origamiInstructionsOpen } = Store.get().main;
	Store.emit('LOCK_SCROLL', !origamiInstructionsOpen);
	Store.get().main.set({ origamiInstructionsOpen: !origamiInstructionsOpen });
});

Store.on('SCROLL_AFTER_PAPER_CLOSE', () => {
	const { scrollDirection } = Store.get().main;
	Store.emit('UPDATE_SCROLL_STAGE', scrollDirection);
});

// AR
Store.on('LAUNCH_AR', (habitat) => {
	Store.get().main.set({
		arLaunched: true,
		arHabitat: habitat,
		arAssetsLoaded: Store.get().main.arAssetsLoaded || {},
		arLoading: true
	});
	Store.emit('TRACK_VIEW', { cat: 'ar-loading' });
});

Store.on('CAMERA_STARTED', () => {
	Store.get().main.set({
		showCameraFeed: true,
		cameraReady: true,
	});
	Store.emit('TRACK_VIEW', { cat: 'ar-camera' });
});

Store.on('REQUESTING_CAMERA_PERMISSIONS', () => {
	Store.get().main.set({ showRequestingPermissions: true });
});

Store.on('CAMERA_PERMISSIONS_ACCEPTED', () => {
	Store.get().main.set({ showRequestingPermissions: false });
	Store.emit('TRACK_CAMERA_PERMISSIONS', 'allow');
});

Store.on('CAMERA_PERMISSIONS_DENIED', () => {
	Store.get().main.set({
		cameraPermissionsDenied: true,
		showRequestingPermissions: false
	});
	Store.emit('TRACK_CAMERA_PERMISSIONS', 'deny');
	Store.emit('TRACK_VIEW', { cat: 'permissions-denied' });
});

Store.on('SHOW_DEVICE_NOT_SUPPORTED', () => {
	Store.get().main.set({ deviceError: true });
	Store.emit('TRACK_VIEW', { cat: 'device-error' });
});

Store.on('AR_ASSET_LOADED', ({ asset, habitatNum, habitatPageData }) => {
	const loadedAssetsForHabitat = Store.get().main.arAssetsLoaded?.[habitatNum] || [];
	const newLoadedAssetsForHabitat = [...loadedAssetsForHabitat, asset];
	Store.get().main.set({
		arAssetsLoaded: {
			...Store.get().main.arAssetsLoaded,
			[habitatNum]: newLoadedAssetsForHabitat
		}
	});
	const neededAssets = getAssetsToLoad(habitatNum, habitatPageData);
	if (newLoadedAssetsForHabitat.length >= neededAssets.length) {
		Store.emit('ALL_AR_ASSETS_LOADED', habitatNum);
	}
});

Store.on('ALL_AR_ASSETS_LOADED', (habitatNum) => {
	Store.get().main.set({
		arLoading: false,
		[`habitat${habitatNum}LoadingComplete`]: true
	});
});

Store.on('TOGGLE_AR_HELP', () => {
	const { arHelpOpen, arMiniHelpOpen } = Store.get().main;
	if (!arHelpOpen && arMiniHelpOpen) {
		Store.emit('TOGGLE_AR_MINI_HELP', { show: false });
	}
	Store.get().main.set({ arHelpOpen: !arHelpOpen });
	if (!arHelpOpen) {
		Store.emit('TRACK_AR_HELP_CLICK');
	}
});

Store.on('TOGGLE_AR_MINI_HELP', ({ show, userDismissed }) => {
	const { arMiniHelpDismissed } = Store.get().main;
	if (show && arMiniHelpDismissed) return;
	Store.get().main.set({ arMiniHelpOpen: show });
	if (userDismissed) {
		Store.get().main.set({ arMiniHelpDismissed: true });
		Store.emit('TRACK_CLOSE_AR_MINI_HELP');
	}
});

Store.on('HABITAT_PLACED', (bool) => {
	Store.get().main.set({ habitatPlaced: bool });
});

Store.on('OPEN_HOTSPOT_VIDEO', ({ video }) => {
	Store.get().main.set({
		activeHotspotVideo: video,
		showHotspotVideo: true
	});
});

Store.on('CLOSE_HOTSPOT_VIDEO', () => {
	Store.get().main.set({
		showHotspotVideo: false
	});
});

Store.on('EXIT_AR', () => {
	Store.get().main.set({
		arHabitat: null,
		arLoading: false,
		showCameraFeed: false,
		cameraReady: false,
		deviceError: false,
		showRequestingPermissions: false
	});
});

Store.on('BACK_TO_HOME', () => {
	Store.emit('EXIT_AR');
	Store.emit('UPDATE_SCROLL_STAGE', LANDING);
});


// SPLASH
Store.on('SPLASH_ASSET_LOADED', (asset) => {
	const loadedAssets = Store.get().main.splashAssetsLoaded || [];
	const newLoadedAssets = [...loadedAssets, asset];
	Store.get().main.set({ splashAssetsLoaded: newLoadedAssets });
	const neededAssets = getSplashAssetsToLoad();
	if (newLoadedAssets.length >= neededAssets.length) {
		Store.emit('ALL_SPLASH_ASSETS_LOADED');
	}
});

Store.on('ALL_SPLASH_ASSETS_LOADED', () => {
	Store.get().main.set({ splashReady: true });
});